/**
 * The bill, on screen, with a way to print it and a way to refund it.
 *
 * WHY REFUNDING LIVES HERE
 * ------------------------
 * It is where someone already is. A customer complains, the cashier pulls up
 * the bill to check it, and the decision follows immediately. Sending them to
 * a separate screen to find the same order again is how the wrong one gets
 * refunded.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { Bill } from './Bill.jsx';
import { BillDiscount } from './BillDiscount.jsx';
import { DiscountAudit } from './DiscountAudit.jsx';
import { RefundPanel } from './RefundPanel.jsx';
import { ItemRefundPanel } from './ItemRefundPanel.jsx';
import { AdminDiscount, AdminCancel, AdminPaymentMethod } from './AdminCorrection.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';

function tradingDayLabel(ymd) {
  const [y, m, d] = String(ymd).split('-').map(Number);
  if (!y || !m || !d) return ymd;
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

export function BillSheet({ orderId, onClose, canRefund, canDiscount = false, admin = false, printOnLoad = false }) {
  const client = useQueryClient();
  const [refunding, setRefunding] = useState(false);
  const [itemRefunding, setItemRefunding] = useState(false);
  const [discounting, setDiscounting] = useState(false);
  // The admin's corrections: 'discount', 'payment' or 'cancel'. See AdminCorrection.jsx.
  const [correcting, setCorrecting] = useState(null);

  const {
    data: bill,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['bill', orderId],
    queryFn: () => api.get(`/pos/orders/${orderId}/bill`),
    enabled: Boolean(orderId),
  });

  // Print the receipt without being asked, when the shop has turned that on and
  // this bill was opened by a payment just going through. Once only, and after
  // the bill has painted, so the print dialog does not freeze a half-drawn page.
  const printedRef = useRef(false);
  useEffect(() => {
    if (printOnLoad && bill && !printedRef.current) {
      printedRef.current = true;
      const timer = setTimeout(() => window.print(), 150);
      return () => clearTimeout(timer);
    }
  }, [printOnLoad, bill]);

  const refund = useMutation({
    mutationFn: (body) => api.post(`/pos/orders/${orderId}/refund`, body),
    onSuccess: () => {
      // The bill now says REFUNDED, and the shift's takings have changed.
      client.invalidateQueries({ queryKey: ['bill', orderId] });
      client.invalidateQueries({ queryKey: ['shift'] });
      client.invalidateQueries({ queryKey: ['orders'] });
      setRefunding(false);
    },
  });

  const refundItem = useMutation({
    mutationFn: ({ itemId, ...body }) =>
      api.post(`/pos/orders/${orderId}/items/${itemId}/refund`, body),
    onSuccess: () => {
      // The refunded line now shows struck through and the balance has dropped;
      // the shift's takings and the orders list have moved with it. The panel
      // stays open so a second unhappy line can be handed back too.
      client.invalidateQueries({ queryKey: ['bill', orderId] });
      client.invalidateQueries({ queryKey: ['shift'] });
      client.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  const applyDiscount = useMutation({
    mutationFn: ({ discount, approval }) =>
      api.post(`/pos/orders/${orderId}/discount`, { discount, ...(approval ? { approval } : {}) }),
    onSuccess: () => {
      // The bill's total drops; the board and any open figures follow.
      client.invalidateQueries({ queryKey: ['bill', orderId] });
      client.invalidateQueries({ queryKey: ['pos', 'open-orders'] });
      client.invalidateQueries({ queryKey: ['shift'] });
      setDiscounting(false);
    },
  });

  const settle = () => {
    client.invalidateQueries({ queryKey: ['bill', orderId] });
    client.invalidateQueries({ queryKey: ['orders'] });
    client.invalidateQueries({ queryKey: ['shift'] });
    client.invalidateQueries({ queryKey: ['reports'] });
    client.invalidateQueries({ queryKey: ['pos', 'open-orders'] });
    setCorrecting(null);
  };
  const adminDiscount = useMutation({ mutationFn: (body) => api.post(`/pos/orders/${orderId}/admin-discount`, body), onSuccess: settle });
  const adminCancel = useMutation({ mutationFn: (reason) => api.post(`/pos/orders/${orderId}/void`, { reason }), onSuccess: settle });
  const adminPayment = useMutation({ mutationFn: (body) => api.post(`/pos/orders/${orderId}/admin-payment-method`, body), onSuccess: settle });

  // A discount can only go on a bill nobody has paid yet — once it is paid,
  // taking money off is a refund, which has its own button.
  const unpaid = bill && bill.invoice?.paymentStatus !== 'paid';
  // A single item can be handed back on a paid bill that has not already been
  // refunded whole — the case the customer is unhappy with one of several dishes.
  const paid = bill && bill.invoice?.paymentStatus === 'paid';
  const wholeRefunded = bill && bill.invoice?.status === 'refunded';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Bill"
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/70 p-4"
    >
      <div className="w-full max-w-sm rounded-2xl bg-surface p-4">
        {isLoading && (
          <div className="flex h-40 items-center justify-center">
            <Spinner label="Loading the bill" />
          </div>
        )}

        {isError && (
          <p className="py-8 text-center text-sm text-ink-2">
            Could not load this bill. Check the branch computer is running.
          </p>
        )}

        {bill && !refunding && !itemRefunding && !discounting && !correcting && (
          <>
            <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-rule">
              <Bill bill={bill} />
            </div>

            {/* Which trading day the bill counts under: a 1 am bill belongs to
                the night before. On screen for staff; the printed bill keeps
                the clock time. */}
            {bill.invoice?.businessDate && (
              <p className="no-print mt-2 text-xs text-ink-3">
                Trading day: <span className="font-medium text-ink-2">{tradingDayLabel(bill.invoice.businessDate)}</span>
              </p>
            )}

            {/* Staff-only, and deliberately outside the printable bill: who gave
                any discount, for the person looking the order up — not for the
                customer's receipt. */}
            <DiscountAudit discounts={bill.totals?.discounts} cashier={bill.cashier} />

            {/* On an unpaid bill only — a discount on a paid one is a refund. */}
            {canDiscount && unpaid && (
              <div className="no-print mt-3">
                <Button variant="secondary" className="w-full" onClick={() => setDiscounting(true)}>
                  Give a discount
                </Button>
              </div>
            )}

            {/* Hand back one line on a paid bill — the customer is unhappy with
                one dish, not all of them. Not once the whole bill is refunded. */}
            {canRefund && paid && !wholeRefunded && (
              <div className="no-print mt-3">
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => setItemRefunding(true)}
                >
                  Refund an item
                </Button>
              </div>
            )}

            {/* The owner's corrections, on any bill that is still standing. */}
            {admin && bill.invoice?.status !== 'cancelled' && bill.invoice?.status !== 'voided' && (
              <div className="no-print mt-3 flex flex-wrap gap-2">
                <Button variant="secondary" className="flex-1" onClick={() => setCorrecting('discount')}>
                  Correct discount
                </Button>
                {movablePayment(bill) && (
                  <Button variant="secondary" className="flex-1" onClick={() => setCorrecting('payment')}>
                    Move payment
                  </Button>
                )}
                <Button variant="secondary" className="flex-1" onClick={() => setCorrecting('cancel')}>
                  Cancel bill
                </Button>
              </div>
            )}

            <div className="no-print mt-2 flex gap-2">
              <Button variant="secondary" size="lg" onClick={onClose}>
                Close
              </Button>

              {canRefund && bill.invoice?.status !== 'refunded' && (
                <Button variant="secondary" size="lg" onClick={() => setRefunding(true)}>
                  Refund
                </Button>
              )}

              <Button size="lg" className="flex-1" onClick={() => window.print()}>
                Print
              </Button>
            </div>
          </>
        )}

        {bill && discounting && (
          <BillDiscount
            busy={applyDiscount.isPending}
            error={applyDiscount.error}
            onApply={(discount, approval) => applyDiscount.mutate({ discount, approval })}
            onCancel={() => {
              applyDiscount.reset();
              setDiscounting(false);
            }}
          />
        )}

        {bill && correcting === 'discount' && (
          <AdminDiscount
            current={currentDiscount(bill)}
            total={Number(bill.totals?.grandTotal ?? 0)}
            busy={adminDiscount.isPending}
            error={adminDiscount.error?.message}
            onApply={(body) => adminDiscount.mutate(body)}
            onCancel={() => setCorrecting(null)}
          />
        )}
        {bill && correcting === 'payment' && (
          <AdminPaymentMethod
            current={movablePayment(bill).method}
            total={Number(bill.totals?.grandTotal ?? 0)}
            busy={adminPayment.isPending}
            error={adminPayment.error?.message}
            onApply={(body) => adminPayment.mutate(body)}
            onCancel={() => setCorrecting(null)}
          />
        )}
        {bill && correcting === 'cancel' && (
          <AdminCancel
            orderNumber={bill.invoice?.number ?? bill.invoice?.orderNumber ?? ''}
            busy={adminCancel.isPending}
            error={adminCancel.error?.message}
            onConfirm={(reason) => adminCancel.mutate(reason)}
            onCancel={() => setCorrecting(null)}
          />
        )}
        {bill && refunding && (
          <RefundPanel
            total={bill.totals?.grandTotal ?? 0}
            invoiceNumber={bill.invoice?.number}
            busy={refund.isPending}
            error={refund.error}
            onCancel={() => {
              refund.reset();
              setRefunding(false);
            }}
            onRefund={(body) => refund.mutate(body)}
          />
        )}

        {bill && itemRefunding && (
          <ItemRefundPanel
            items={bill.items}
            totals={bill.totals}
            invoiceNumber={bill.invoice?.number}
            busy={refundItem.isPending}
            error={refundItem.error}
            onCancel={() => {
              refundItem.reset();
              setItemRefunding(false);
            }}
            onRefund={(itemId, body) => refundItem.mutate({ itemId, ...body })}
          />
        )}
      </div>
    </div>
  );
}

/** The discount as the bill carries it now, in the form the correction panel edits. */
/** How a paid bill stands, if an admin may move it: one cash, card or online payment, or a Foodpanda bill (owed, no payment). */
function movablePayment(bill) {
  if (bill.invoice?.paymentStatus !== 'paid') return null;
  const done = (bill.payments ?? []).filter((p) => !p.status || p.status === 'completed');
  if (bill.invoice?.type === 'foodpanda' && done.length === 0) return { method: 'foodpanda' };
  if (done.length !== 1 || !['cash', 'card', 'online'].includes(done[0].method)) return null;
  return done[0];
}

function currentDiscount(bill) {
  const d = (bill.totals?.discounts ?? [])[0];
  if (!d) return null;
  const type = d.type ?? d.discountType ?? d.discount_type;
  const value = Number(d.value ?? d.discountValue ?? d.discount_value);
  return type && value > 0 ? { type, value } : null;
}
